import { PRICE_REPRESENTATION_MULTIPLIER, type Pricing } from './Pricing.ts';
import type { PromotionGroup } from './Promotion.ts';
import type { Subscription } from './Subscription.ts';
import type { Specifications } from './Specification.ts';
import type { Categories, Category, CategoryCode } from './Category.ts';
import type { GroupedProductsByCollection } from '@/widgets/services/CategoryService.ts';
import type { ProductTranslations } from './config/index.ts';

export type ProductType = 'machine' | 'capsule' | 'accessory';

export type ProductTechnology = 'original' | 'vertuo' | 'pro';

export type ProductColor = {
  /** Color human name */
  name: string;
  /** Color hex code with hash */
  value: string;
};

export type ProductRating = {
  ratingAverage?: number;
  reviewCount?: number;
};

export type PurchaseDriver = {
  description: string;
};

export type BundleContents = {
  productId: string;
  quantity: number;
};

export type GroupedProduct = {
  type: ProductType;
  productCode: string;
  quantity: number;
};

export type Product = ProductEnrichments & {
  id: string;
  slug: string;
  name: string;
  headline: string;
  description: string;
  originDescription: string;
  roastingDescription: string;
  type: ProductType;
  technologies: ProductTechnology[];
  slides: {
    url: string;
    altText?: string;
  }[];
  mainImage: {
    url: string;
    alt: string;
  };
  cardImage?: {
    url: string;
    alt: string;
  };
  media: {
    url: string;
    altText?: string;
    type: string;
  }[];
  pricing: Pricing;
  unitQuantity: number;
  salesMultiple: number;
  range: Range;
  isOrderable: boolean;
  quantities: number[];
  maxOrderQuantity: number;
  categories: Categories;
  inStock: boolean;
  bundle?: ProductBundle;
  associatedBundles?: Product[];
  model?: Model;
  color?: ProductColor; // TODO: A specification?
  rating?: ProductRating;
  productSelections?: string[];
  segments?: string[];
  subscriptionsByDuration: Record<number, Subscription[]>;
  subscriptionsInfo: Subscription | undefined; // A single subscription associated to the product (if any)
  promotions: PromotionGroup[];
  nessoft: NessoftLegacy;
  seo: SeoMetaData;
  specifications: Specifications;
  highlightLabels: ProductHighlightLabel[];
  displayEcoTax?: boolean; // TODO: Move off main object
  alternativeProducts?: Product[];
  metadata?: ProductMetadata;
  groupedProducts?: GroupedProduct[];
  thirdLineQuantities: number[];
};

export type FavoriteProducts = {
  cartId: string;
  favoriteProducts: Set<string>;
};

export type ProductHighlightLabel = {
  id: string;
  label: string;
  bgColor: string;
  fgColor: string;
};

export type SeoMetaData = {
  description?: string;
};

export type NessoftLegacy = {
  internationalId: string;
  legacyId: string; // This is actually the Nessoft Id
  internationalName: string;
  category: string;
};

export type ProductDiscount = {
  type: 'FIXED_PRICE' | 'PERCENTAGE';
  percentageAmount?: number;
  discountAmount?: number;
};

export type LazyProduct = {
  id: string;
};

export type ProductBundleQuantity = {
  quantityInBundle: number;
};

export type BundleContentsProduct = Product & ProductBundleQuantity;
type BundleContentsLazyProduct = LazyProduct & ProductBundleQuantity;

export type ProductBundle = {
  isAssortment?: boolean;
  soldSeparatelyPrice?: string;
  contents: Array<BundleContentsProduct | BundleContentsLazyProduct>;
};

/* Non-ECAPI enrichment from Product Configurator (Generic CMS) */
export type ProductEnrichments = {
  discount?: ProductDiscount;
  purchaseDrivers?: PurchaseDriver[];
  isSubscriptionOnly?: boolean;
  isAssortment?: boolean;
  soldSeparatelyPrice?: string;
  bundleContents?: BundleContents[];
  hideTechnologyLabelOnProductCards?: boolean;
};

export type ProductEnrichmentsDefinition = {
  translations?: ProductTranslations;
  enrichments?: {
    technologies?: {
      original?: ProductEnrichments;
      vertuo?: ProductEnrichments;
      pro?: ProductEnrichments;
    };
    productTypes?: {
      capsule?: ProductEnrichments;
      machine?: ProductEnrichments;
      accessory?: ProductEnrichments;
    };
    categories?: Record<string, ProductEnrichments>;
    products?: Record<string, ProductEnrichments>;
  };
};

export type ProductMetadata = {
  listPosition: number;
  categoryListPosition: Record<CategoryCode, number>;
};

export type Range = {
  category: string;
  products: Product[];
};

export type Model = {
  category: string;
  ranges: Range[];
};

export type ProductMediaAsset = {
  url: string;
  altText?: string | null;
};

export const MINIMUM_PRODUCT_RATING = 3;
export const MAXIMUM_COFFEE_INTENSITY = 13;
export const MAXIMUM_ALTERNATIVE_PRODUCTS = 2;

export function simplifyTechnologies(
  technologies: string[] = [],
  preferredTechnology?: string
): ProductTechnology[] {
  return technologies
    .toSorted((a, b) => {
      if (preferredTechnology === a) {
        return -1;
      }
      if (preferredTechnology === b) {
        return 1;
      }
      return 0;
    })
    .map((category) => category?.split('/').at(-1) as ProductTechnology);
}

export function getShortId(productId: string = ''): string {
  const parts = productId.split('/');
  return parts.at(-1) ?? productId;
}

export const productTypeToSuperCategoryMap: Record<ProductType, string> = {
  machine: 'machine-range',
  capsule: 'capsule-range',
  accessory: 'accessory-collection'
};

export const productTypeToUrlCompatibleString: Record<ProductType, string> = {
  machine: 'machines',
  capsule: 'capsules',
  accessory: 'accessories'
};

export function productTypeToProductTypePath(productType: ProductType): string {
  if (productType.endsWith('y')) return productType.replace('y', 'ies');
  return `${productType}s`;
}

export function productTypePathToProductType(
  productTypePath: string
): ProductType {
  if (productTypePath.endsWith('ies'))
    return productTypePath.replace('ies', 'y') as ProductType;
  return productTypePath.slice(0, -1) as ProductType;
}

export function resolveProductEnrichments(
  config: ProductEnrichmentsDefinition,
  technology: ProductTechnology,
  productType: ProductType,
  categories: string[],
  productId: string
): ProductEnrichments {
  const matchingCategoryEnrichments: ProductEnrichments[] = categories
    ? Object.entries(config.enrichments?.categories ?? {})
        .filter(([cat]) => categories?.includes(cat))
        .sort(([keyA], [keyB]) => keyA.localeCompare(keyB, 'en-GB')) // Apply in alphabetic order
        .map(([, cfg]) => cfg)
    : [];

  const resolved: ProductEnrichments = {
    ...config?.enrichments?.technologies?.[technology],
    ...config?.enrichments?.productTypes?.[productType],
    ...Object.assign({}, ...matchingCategoryEnrichments),
    ...config?.enrichments?.products?.[productId]
  };

  return {
    ...resolved,
    ...(resolved.discount
      ? {
          discount: {
            ...resolved.discount,
            ...(resolved.discount.discountAmount
              ? {
                  discountAmount:
                    resolved.discount.discountAmount *
                    PRICE_REPRESENTATION_MULTIPLIER
                }
              : {})
          }
        }
      : {})
  };
}

export function strikethroughPriceFromProductDiscount(
  price: number,
  discount: ProductDiscount
): number {
  if (discount.type === 'FIXED_PRICE') {
    if (!discount.discountAmount || discount.discountAmount <= 0) {
      return NaN;
    }
    return price + discount.discountAmount!;
  }

  if (discount.type === 'PERCENTAGE') {
    if (
      !discount.percentageAmount ||
      discount.percentageAmount <= 0 ||
      discount.percentageAmount > 99
    ) {
      return NaN;
    }
    return Math.round(
      (price / (100 - discount.percentageAmount)) *
        (PRICE_REPRESENTATION_MULTIPLIER / 10)
    );
  }

  return NaN;
}

export function hasSubscriptions(product: Product): boolean {
  return Object.values(product.subscriptionsByDuration).flat().length !== 0;
}

export function hasPromotions(product: Product): boolean {
  return product.promotions.length !== 0;
}

export function hasSpecifications(product: Product): boolean {
  return Object.values(product.specifications).length !== 0;
}

export const COMPATIBILITY_RANGE_IDENTIFIER =
  'capsule-range-label-compatibility-vertuo' as const;

// we can add more cup sizes here of compatibility with oversized capsules
const oversizedCapsuleSuperCategories = [
  'capsule-cupSize-carafe-vertuo',
  'capsule-cupSize-XL'
];

export function hasOversizedCapsule(
  categoryList: Categories = new Map()
): boolean {
  return [...categoryList.keys()].some((item) =>
    oversizedCapsuleSuperCategories.some((superCategory) =>
      item.includes(superCategory)
    )
  );
}

/** Capsules tagged to certain range categories will show the machine compatibility button/modal */
export function capsuleHasMachineCompatibilityIssue(ranges: string[]) {
  const rangesSimple = simplifyRangeIds(ranges);
  return rangesSimple.some((id) => id === COMPATIBILITY_RANGE_IDENTIFIER);
}

/**
 * Full range strings are like: 'nesclub2.es.b2c/cat/capsule-range-label-compatibility-vertuo'
 * @returns The range string without the website/market info
 */
function simplifyRangeIds(ranges: string[]) {
  return ranges.map((range) => range.split('/').pop());
}

export function getProductImage(
  { mainImage, cardImage, slides, media, name }: Product,
  imageProp: 'main' | 'card' | 'slide' | 'mediaGallery'
):
  | {
      url: string;
      alt: string;
    }
  | undefined {
  switch (imageProp) {
    case 'slide':
      if (!slides || slides.length === 0) return undefined;
      return {
        url: slides[0].url,
        alt: slides[0].altText || name || ''
      };
    case 'mediaGallery':
      if (!media || media.length === 0) return undefined;
      return {
        url: media[0].url,
        alt: media[0].altText || name || ''
      };
    case 'card':
      if (cardImage) {
        return {
          url: cardImage.url,
          alt: cardImage.alt || name || ''
        };
      }

      if (mainImage) {
        return {
          url: mainImage.url,
          alt: mainImage.alt || name || ''
        };
      }

      return undefined;
    default:
      if (!mainImage) return undefined;

      return {
        url: mainImage.url,
        alt: mainImage.alt || name || ''
      };
  }
}

export function buildProductListUrl(
  market: string,
  locale: string,
  productTypePath: string,
  technology: string
): string {
  return `/${market}/${locale}/order/${productTypePath}/${technology}`;
}

export function buildProductUrl(
  market: string,
  locale: string,
  productTypePath: string,
  technology: string,
  slug: string
): string {
  return `${buildProductListUrl(market, locale, productTypePath, technology)}/${slug}`;
}

export function groupProductsByCollection(
  products: Product[],
  collections: Category[]
) {
  return collections.reduce<GroupedProductsByCollection>((acc, collection) => {
    const matchingProducts = products
      .filter((p) => p.categories.has(collection.code))
      .sort(collectionProductSort(collection.code));
    if (matchingProducts.length > 0) {
      acc[collection.code] = matchingProducts;
    }
    return acc;
  }, {});
}

export function productBundleDataIsResolved(
  data: ProductBundle['contents'][number] | null
): data is Product & ProductBundleQuantity {
  return data ? Object.keys(data).length > 2 : false;
}

export function productIsLazy(
  data: ProductBundle['contents'][number] | null
): data is LazyProduct & ProductBundleQuantity {
  return data
    ? Object.keys(data).length === 2
    : false; /** Lazy products are products with only id and quantity props */
}

/**
 * Products can be filtered by BTG rule 'Selections', against a logged in customer
 */
export function excludeByProductBTGRules(
  productSelections?: string[],
  customerSelections?: string[]
): boolean {
  if (!productSelections?.length || !customerSelections?.length) {
    // Don't exclude if there are not any rules
    return false;
  }

  for (const selection of customerSelections) {
    if (productSelections.includes(selection)) return false;
  }

  return true;
}

/**
 * Products can be filtered by Customer 'Segments', against a logged in customer
 */
export function excludeByCustomerSegments(
  productSegments?: string[] | null,
  customerSegments?: string[] | null
): boolean {
  // Don't exclude products without segments
  if (!productSegments?.length) return false;

  for (const segment of customerSegments ?? []) {
    if (productSegments?.includes(segment)) return false;
  }

  return true;
}

function collectionProductSort(collection: CategoryCode) {
  return (productA: Product, productB: Product): number =>
    (productA.metadata?.categoryListPosition[collection] ?? 0) -
    (productB.metadata?.categoryListPosition[collection] ?? 0);
}

/** Show all accessory products on all PLPs but filter others if they are not in the current technology */
export function shouldShowProductInPlp(
  productTypeAtPageLevel: ProductType,
  productTypeAtProductLevel: Product['type'],
  technologiesAtProductLevel: Product['technologies'],
  technologyAtPageLevel?: ProductTechnology
): boolean {
  if (productTypeAtPageLevel === 'accessory') {
    return true;
  }

  if (
    technologyAtPageLevel === undefined ||
    productTypeAtProductLevel === 'accessory'
  ) {
    return true;
  }

  return technologiesAtProductLevel.includes(technologyAtPageLevel);
}
